import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import { useAxios } from '../Components/axiosContext.js';
import {
  fetchAllReportIds,
  selectAllReportIds,
  selectAllReportDates,
  selectReportsLoading,
  selectReportsError,
  selectReportsHasFetched,
} from '../redux/slices/reportSlice.js';

// Global flag to prevent simultaneous fetches
let globalFetchInProgress = false;

export const useReportIds = () => {
  const dispatch = useDispatch();
  const { user } = useUser();
  const axios = useAxios();

  /* ────────────────────────────────────────
     refs for cross-render state
  ──────────────────────────────────────── */
  const isPatchingRef = useRef(false); // in-flight Clerk patch
  const lastSyncedKeyRef = useRef(null); // ▶ remembers last synced IDs

  /* ────────────────────────────────────────
     Redux state (API = single source of truth)
  ──────────────────────────────────────── */
  const reportIds = useSelector(selectAllReportIds);
  const reportDates = useSelector(selectAllReportDates);
  const isLoading = useSelector(selectReportsLoading);
  const error = useSelector(selectReportsError);
  const hasFetched = useSelector(selectReportsHasFetched);

  /* ────────────────────────────────────────
     1️⃣ Fetch IDs from API (once)
  ──────────────────────────────────────── */
  useEffect(() => {
    if (user?.id && axios && !hasFetched && !isLoading && !globalFetchInProgress) {
      globalFetchInProgress = true;
      dispatch(fetchAllReportIds(axios)).finally(() => {
        globalFetchInProgress = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hasFetched, isLoading]);

  /* ────────────────────────────────────────
     2️⃣ Sync IDs → Clerk user.metadata  (idempotent)
  ──────────────────────────────────────── */
  useEffect(() => {
    if (!user?.id || !hasFetched) return; // nothing to do
    if (isPatchingRef.current) return; // already in flight

    // build the subset we actually store
    const nextIds = {
      INTAKE: reportIds.INTAKE || null,
      INTAKE1: reportIds.INTAKE1 || null,
      INTAKE2: reportIds.INTAKE2 || null,
      INTAKE3: reportIds.INTAKE3 || null,
    };
    const nextKey = JSON.stringify(nextIds); // ▶ stable hash

    // stop if we already synced this exact set
    if (lastSyncedKeyRef.current === nextKey) return;

    // compare with Clerk's current metadata
    const clerkIds = {
      INTAKE: user.unsafeMetadata?.reportId ?? null,
      INTAKE1: user.unsafeMetadata?.reportIdIntake1 ?? null,
      INTAKE2: user.unsafeMetadata?.reportIdIntake2 ?? null,
      INTAKE3: user.unsafeMetadata?.reportIdIntake3 ?? null,
    };
    if (nextKey === JSON.stringify(clerkIds)) {
      lastSyncedKeyRef.current = nextKey; // already equal
      return;
    }

    // patch Clerk (once)
    isPatchingRef.current = true;
    user
      .update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          reportId: nextIds.INTAKE,
          reportIdIntake1: nextIds.INTAKE1,
          reportIdIntake2: nextIds.INTAKE2,
          reportIdIntake3: nextIds.INTAKE3,
        },
      })
      .finally(() => {
        isPatchingRef.current = false;
        // wait for Clerk to re-emit the updated user before marking synced
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(reportIds), hasFetched, user?.id]);

  /* ────────────────────────────────────────
     Memoised return values
  ──────────────────────────────────────── */
  const effectiveReportIds = useMemo(
    () => ({
      INTAKE: reportIds.INTAKE,
      INTAKE1: reportIds.INTAKE1,
      INTAKE2: reportIds.INTAKE2,
      INTAKE3: reportIds.INTAKE3,
    }),
    [reportIds],
  );

  const effectiveReportDates = useMemo(
    () => ({
      INTAKE: reportDates.INTAKE,
      INTAKE1: reportDates.INTAKE1,
      INTAKE2: reportDates.INTAKE2,
      INTAKE3: reportDates.INTAKE3,
    }),
    [reportDates],
  );

  return {
    reportIds: effectiveReportIds,
    reportDates: effectiveReportDates,
    isLoading,
    error,
    hasFetched,
    // legacy shim
    fetchReportIdsByType: () => null,
  };
};
